import React, { useCallback } from 'react';
import classNames             from 'classnames';
import DV                     from 'ts-data-validator';
import { NavLink, useParams } from 'react-router-dom';
import { useTranslation }     from 'react-i18next';

import usePrevious from '../../../../common/hooks/usePrevious';

import needReset       from '../../../common/tools/need-reset';
import ContactUs       from '../../../components/buttons/ContactUs';
import Meta            from '../../../components/meta';
import Preloader       from '../../../components/containers/preloader';
import ReadingTimeIcon from '../../../components/icons/ReadingTimeIcon';
import { useCatalog }  from '../../../providers/catalog';
import { useNews }     from '../../../providers/news';

import renderStaticSection from '../../_common/utils/renderStaticSection';
import Notfound            from '../../notfound/components/Notfound';

const NewsDetail = () => {
  const { urlKey } = useParams();

  const {
          getCatalogItems,
          resetFilter,
        } = useCatalog();

  const {
          getNewsDetail,
          resetNewsDetail,
          article,
          detailLoaded,
          loading,
        } = useNews();

  const {
          createdAtIso,
          headImage,
          label,
          meta: {
                  title,
                  meta: {
                          canonical,
                          description,
                          keywords,
                        },
                },
          contactButtonName,
          updatedAtIso,
        }     = article;

  // headImage — resolved путь на CDN без расширения (см. TopNews.js: `${headImage}.webp`)
  const metaImages = headImage ? [{ alt: label || '', src: `${headImage}.webp`, type: 'image/webp' }] : undefined;
  const { t } = useTranslation();

  const prevUrl = usePrevious(urlKey);

  React.useEffect(() => {
    if (!detailLoaded) {
      void getNewsDetail(urlKey);
    }

    if (!isSSR()) {
      scrollTo({
        top:  0,
        left: 0,
      });
    }
    return () => {
      void resetNewsDetail();
    };
  }, []);

  React.useEffect(() => {
    if (prevUrl !== urlKey && !loading) {
      void getNewsDetail(urlKey)
        .then(() => {
          scrollTo({
            top:  0,
            left: 0,
          });
        });
    }
  }, [urlKey]);

  const renderPage = useCallback(() => {
    if (
      detailLoaded &&
      DV.isNull(article?.label) &&
      DV.isNull(article?.headImage)
    ) {
      return <Notfound />;
    }

    let content = {};

    try {
      content = JSON.parse(article?.content);
    } catch (e) {
      return <Notfound />;
    }

    if (!detailLoaded) {
      return null;
    }

    return (
      <main className={'newsDetail'}>
        <div className={'main'}>
          <div
            className={'article'}
            itemScope
            itemType={'http://schema.org/Article'}
          >
            <meta itemProp={'datePublished'} content={article?.createdAt} />
            <meta
              content={article?.headImage}
              itemProp={'image'}
              itemType={'http://schema.org/ImageObject'}
            />
            <section>
              <div className={'container'}>
                <h1 itemProp={'headline'}>{article?.label}</h1>
              </div>
              <div className={'news_date'}>
                <p className={'news_create_time'}>{article?.createdAt}</p>
                {article?.readingTime && (
                  <div className={'reading_time_wrapper'}>
                    <ReadingTimeIcon />
                    <p className={'reading_time'}>{article?.readingTime}</p>
                  </div>)}
              </div>
            </section>
            <article itemProp={'articleBody'} className={'articleBody'}>
              {content?.SECTIONS && content.SECTIONS.map((section, s) =>
                renderStaticSection(section, s),
              )}
            </article>
            <div className={'buttons_container'}>
              <NavLink
                className={classNames('btn', 'fill')}
                to={article?.buttonLink}
                onClick={() => {
                  if (needReset(article?.buttonLink)) {
                    void resetFilter();
                    void getCatalogItems(article?.buttonLink);
                  }
                }}
              >
                {article?.buttonName}
              </NavLink>
              <NavLink
                className={
                  classNames('btn', 'primary')}
                to={'/vhodnye-dveri'}
                onClick={() => {
                  void resetFilter();
                  void getCatalogItems('/vhodnye-dveri');
                }}
              >{t('label_goToCatalog')}</NavLink>
              <ContactUs label={contactButtonName || t('btn_get_advice')} />
            </div>
          </div>
        </div>
      </main>
    );
  }, [detailLoaded, article, loading]);

  return (
    <>
      <div className={'page-content'}>
        <Meta
          article={{
            author:        t('steelline'),
            modifiedTime:  updatedAtIso,
            publishedTime: createdAtIso,
            section:       'Новости',
          }}
          canonical={canonical}
          description={description}
          images={metaImages}
          keywords={keywords}
          title={title}
          type={'article'}
        />
        {renderPage()}
      </div>
      {(
        !detailLoaded || loading) && <Preloader />}
    </>
  );
};

export default NewsDetail;